import { useCallback, useEffect, useRef, useState } from "react";
import "@corti/dictation-web";
import { axiosPrescription } from "../../../api/axios";

const getTranscriptData = (event) => {
  const detail = event?.detail || {};
  return detail.data || detail;
};

const formatDictationError = (error) => {
  const message = error?.response?.data?.message
    || error?.detail?.message
    || error?.message
    || "";
  const normalized = String(message).toLowerCase();

  if (error?.name === "NotAllowedError" || normalized.includes("permission") || normalized.includes("denied")) {
    return "Micro refusé. Autorisez le microphone puis relancez la dictée.";
  }
  if (error?.name === "AbortError" || normalized.includes("canceled")) return "";
  return message || "Impossible de démarrer la dictée.";
};

export default function useCortiDictation({ language, onTranscript }) {
  const dictationRef = useRef(null);
  const tokenAbortRef = useRef(null);
  const operationRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const transcriptCallbackRef = useRef(onTranscript);
  const finalSegmentsRef = useRef(new Set());
  const lastFinalTranscriptRef = useRef({ text: "", at: 0 });
  const statusRef = useRef("idle");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  transcriptCallbackRef.current = onTranscript;

  const setDictationStatus = (nextStatus) => {
    statusRef.current = nextStatus;
    setStatus(nextStatus);
  };
  const dictationLanguage = String(language || "fr").toLowerCase().startsWith("en") ? "en" : "fr";

  const stopDictation = useCallback(async ({ silent = false } = {}) => {
    stopRequestedRef.current = true;
    tokenAbortRef.current?.abort();
    tokenAbortRef.current = null;

    const dictation = dictationRef.current;
    if (!dictation) {
      setDictationStatus("disconnected");
      return;
    }

    try {
      if (!silent && ["recording", "connecting"].includes(statusRef.current)) {
        setDictationStatus("connecting");
      }
      dictation.stopRecording?.();
      await dictation.closeConnection?.();
      setDictationStatus("disconnected");
    } catch (stopError) {
      if (!silent) {
        setDictationStatus("error");
        setError(formatDictationError(stopError));
      }
    }
  }, []);

  const startDictation = useCallback(async () => {
    if (operationRef.current || statusRef.current === "recording") return;
    const dictation = dictationRef.current;
    if (!dictation) return;

    if (!window.navigator?.mediaDevices?.getUserMedia) {
      setDictationStatus("error");
      setError("Le navigateur ne supporte pas l'accès au microphone.");
      return;
    }

    operationRef.current = true;
    stopRequestedRef.current = false;
    const controller = new AbortController();
    tokenAbortRef.current = controller;

    try {
      setDictationStatus("connecting");
      setError("");
      finalSegmentsRef.current.clear();
      lastFinalTranscriptRef.current = { text: "", at: 0 };

      const response = await axiosPrescription.post(
        "/prescriptions/corti/browser-token",
        { language: dictationLanguage },
        { signal: controller.signal },
      );
      if (stopRequestedRef.current) return;

      const token = response?.data?.token;
      if (!token) throw new Error("Token Corti manquant.");

      dictation.accessToken = token;
      dictation.settingsEnabled = [];
      dictation.allowButtonFocus = true;
      dictation.pushToTalkKeybinding = undefined;
      dictation.toggleToTalkKeybinding = undefined;
      dictation.dictationConfig = {
        automaticPunctuation: true,
        interimResults: true,
        primaryLanguage: dictationLanguage,
        spokenPunctuation: true,
      };
      dictation.startRecording?.();
      setDictationStatus("recording");
    } catch (startError) {
      const message = formatDictationError(startError);
      setDictationStatus(message ? "error" : "disconnected");
      setError(message);
    } finally {
      operationRef.current = false;
      tokenAbortRef.current = null;
    }
  }, [dictationLanguage]);

  const toggleDictation = useCallback(async () => {
    if (["recording", "connecting"].includes(statusRef.current)) {
      await stopDictation();
      return;
    }
    await startDictation();
  }, [startDictation, stopDictation]);

  useEffect(() => {
    const dictation = dictationRef.current;
    if (!dictation) return undefined;

    const handleTranscript = (event) => {
      const data = getTranscriptData(event);
      const text = String(data?.text || "").trim();
      if (!text) return;
      if (!data?.isFinal) return;

      const segmentKey = `${text}|${data?.start ?? ""}|${data?.end ?? ""}`;
      const now = Date.now();
      const last = lastFinalTranscriptRef.current;
      if (finalSegmentsRef.current.has(segmentKey) || (last.text === text && now - last.at < 2000)) return;

      finalSegmentsRef.current.add(segmentKey);
      if (finalSegmentsRef.current.size > 120) {
        finalSegmentsRef.current.delete(finalSegmentsRef.current.values().next().value);
      }
      lastFinalTranscriptRef.current = { text, at: now };
      transcriptCallbackRef.current?.(text);
    };

    const handleRecordingState = (event) => {
      const recordingState = event?.detail?.state;
      if (recordingState === "initializing" || recordingState === "stopping") {
        setDictationStatus("connecting");
      } else if (recordingState === "recording") {
        setDictationStatus("recording");
      } else if (recordingState === "stopped") {
        setDictationStatus("disconnected");
      }
    };

    const handleDictationError = (event) => {
      const message = formatDictationError(event);
      if (!message) return;
      setDictationStatus("error");
      setError(message);
    };

    dictation.addEventListener("transcript", handleTranscript);
    dictation.addEventListener("recording-state-changed", handleRecordingState);
    dictation.addEventListener("error", handleDictationError);
    return () => {
      dictation.removeEventListener("transcript", handleTranscript);
      dictation.removeEventListener("recording-state-changed", handleRecordingState);
      dictation.removeEventListener("error", handleDictationError);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) stopDictation({ silent: true });
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [stopDictation]);

  useEffect(() => () => {
    tokenAbortRef.current?.abort();
    const dictation = dictationRef.current;
    try {
      dictation?.stopRecording?.();
      Promise.resolve(dictation?.closeConnection?.()).catch(() => {});
    } catch {
      // The browser may already have closed the audio connection.
    }
  }, []);

  return { dictationRef, status, error, toggleDictation };
}